import { useMemo } from 'react';
import { useSelectedElementId, useSelectedVersion } from '@client/store/ui';
import { useProjectState } from '@client/store/project';
import { useResolvedElement } from '@client/hooks/use-resolved-version.hooks';
import { projectGuards } from '@shared/helpers/guards/project-guards.helpers';
import { getStashedElementsByRowId } from '@shared/helpers/candidate-discard.helpers';
import type { CandidateRow } from '@shared/models/element-import.model';
import type { IElement } from '@shared/models/project.model';
import { useElementImports } from './use-element-imports.hook';
import { useImportedElements } from './candidate-status.helpers';
import { useVisibleListRowIdsSnapshot } from './list-selection.store';

export type SelectedCandidateRow = {
  row: CandidateRow;
  /** Live or stashed element when the row has been materialized. */
  importedElement: IElement | undefined;
};

/**
 * Resolves the CSV candidate row behind the current element selection (row id
 * or materialized element id). Returns `undefined` when the selection is an
 * import group header, not a candidate row, or hidden by search/status.
 */
export const useSelectedCandidateRow = (): SelectedCandidateRow | undefined => {
  const selectedId = useSelectedElementId();
  const { project } = useProjectState('project');
  const imports = useElementImports(project?.id ?? -1);
  const version = useSelectedVersion();
  const resolvedSelectedElement = useResolvedElement(selectedId);
  const { ids: visibleCandidateIds, reported: visibleCandidatesReported } =
    useVisibleListRowIdsSnapshot();

  const selectedImport =
    imports.status === 'ready' && selectedId
      ? imports.data.importsById[selectedId]
      : undefined;

  const importedElements = useImportedElements(version);
  const liveElement = useMemo(() => {
    if (selectedImport || !selectedId) return undefined;
    if (
      projectGuards.is.element(resolvedSelectedElement) &&
      resolvedSelectedElement.origin?.kind === 'csvImport'
    ) {
      // Only an import in this proposal's view; another proposal's is not.
      const inView = importedElements.get(
        resolvedSelectedElement.origin.candidateRowId,
      );
      return inView?.id === resolvedSelectedElement.id
        ? resolvedSelectedElement
        : undefined;
    }
    return importedElements.get(selectedId);
  }, [selectedImport, selectedId, resolvedSelectedElement, importedElements]);

  const stashedElement = useMemo(() => {
    if (liveElement || selectedImport || !selectedId) return undefined;
    const byRowId = getStashedElementsByRowId(version);
    const byRow = byRowId.get(selectedId);
    if (byRow) return byRow;
    for (const el of byRowId.values()) {
      if (el.id === selectedId) return el;
    }
    return undefined;
  }, [liveElement, selectedImport, selectedId, version]);

  const importedElement = liveElement ?? stashedElement;
  const rowId = importedElement?.origin?.candidateRowId ?? selectedId;

  const row = useMemo(() => {
    if (!rowId || selectedImport || imports.status !== 'ready')
      return undefined;
    for (const rows of Object.values(imports.data.rowsByImport)) {
      const found = rows.find((r) => r.id === rowId);
      if (found) return found;
    }
    return undefined;
  }, [rowId, selectedImport, imports]);

  if (!row) return undefined;
  if (visibleCandidatesReported && !visibleCandidateIds.has(row.id)) {
    return undefined;
  }
  return { row, importedElement };
};
